/**
 * Ertzyx Institution Console — pearl-enterprise palette (light/opal mode).
 * Cool pearl surfaces use violet as the primary identity accent, aqua as the
 * operational counterpoint, and restrained champagne for premium states.
 * Consumed by makeInstitutionShellTokens (src/lib/shell/tokens.ts) and
 * getInstitutionSurface/getInstitutionZonePalette (src/lib/institution-surface.ts);
 * mirrored inline where individual institution components style their own cards.
 * Dark/aurora mode is intentionally untouched by this palette.
 *
 * Five distinct surface tiers separate the sidebar, app canvas, content, cards,
 * and context rail. Green is deliberately reserved for semantic success states.
 */
export const INSTITUTION_HERITAGE_LIGHT = {
  // Five luminous tiers, from the outer canvas through elevated glass cards.
  sidebarBg: '#F8F8FC',
  appBg: '#F7F7FA',
  contentBg: '#FAFAFC',
  cardBg: 'rgba(255,255,255,0.9)',
  cardBgOpaque: '#FFFFFF',
  mutedSurface: 'rgba(248,246,252,0.9)',
  railBg: 'rgba(250,250,252,0.94)',

  // Text
  textPrimary: '#252337',
  textSecondary: '#646174',
  // Darker than the supplied #858194 so small metadata remains AA-readable.
  textMuted: '#706B7D',

  // Borders and separators
  border: 'rgba(92,82,124,0.17)',
  borderStrong: 'rgba(103,82,151,0.3)',
  divider: 'rgba(92,82,124,0.11)',

  // Primary institution identity: lavender/violet.
  lavender: '#C8B6FF',
  violet: '#715AA5',
  violetDeep: '#5E477F',
  violetTint: '#F0EBFC',

  // Secondary operational accents.
  aqua: '#A9E5EA',
  aquaAccent: '#4FA9B2',
  aquaDeep: '#2B6E73',
  aquaTint: '#E7F7F8',
  blush: '#F3C1DB',
  blushAccent: '#C7789F',
  blushDeep: '#8A516D',
  blushTint: '#FBEAF3',
  sky: '#B7D8F5',
  skyDeep: '#3B6C96',

  // Semantic green — success and verified states only.
  green: '#28735F',
  greenDeep: '#205B4C',
  greenAccent: '#31866F',
  greenTint: '#E5F3EE',

  // Champagne stays precious and local to heritage/readiness/premium states.
  champagne: '#E2C477',
  gold: '#A88135',
  goldBright: '#D8B969',
  goldDeep: '#795F25',
  goldTint: '#FBF4DF',

  // Soft violet-neutral depth; never a gray drop-shadow slab.
  shadow:
    '0 1px 2px rgba(45,40,70,0.04), 0 8px 26px rgba(75,65,120,0.06), 0 18px 50px rgba(120,100,180,0.04)',
} as const;

/**
 * Restrained metallic gold sheen for tiny premium accents only: a hero top rule,
 * a progress-bar leading edge, an active-nav micro-indicator, a premium badge
 * border, a small emblem detail. Never a large fill.
 */
export const INSTITUTION_GOLD_SHEEN =
  'linear-gradient(110deg, #795F25 0%, #A88135 35%, #E2C477 50%, #A88135 65%, #795F25 100%)';

/** Low-opacity chromatic edge used only on major framed surfaces. */
export const INSTITUTION_OPALESCENT_EDGE =
  'linear-gradient(100deg,rgba(200,182,255,0.78) 0%,rgba(169,229,234,0.74) 34%,rgba(243,193,219,0.62) 66%,rgba(226,196,119,0.68) 88%,rgba(255,255,255,0.92) 100%)';

/** Mother-of-pearl reflection: atmosphere, not a painted rainbow fill. */
export const INSTITUTION_PEARL_REFLECTION =
  'radial-gradient(circle at 12% 15%,rgba(180,165,255,0.16),transparent 34%),radial-gradient(circle at 78% 20%,rgba(126,218,230,0.12),transparent 36%),radial-gradient(circle at 65% 82%,rgba(244,187,221,0.1),transparent 35%),radial-gradient(circle at 28% 78%,rgba(231,200,116,0.09),transparent 34%)';

/** The identity hero's denser pearl reflection, kept reusable and centralized. */
export const INSTITUTION_PEARL_HERO =
  'radial-gradient(circle at 9% 12%,rgba(200,182,255,0.2),transparent 34%),radial-gradient(circle at 86% 18%,rgba(169,229,234,0.17),transparent 35%),radial-gradient(circle at 72% 88%,rgba(243,193,219,0.12),transparent 34%),linear-gradient(125deg,rgba(255,255,255,0.97),rgba(239,232,255,0.78) 30%,rgba(224,247,247,0.72) 58%,rgba(255,239,220,0.68) 82%,rgba(255,255,255,0.96))';

/**
 * Institution shell palette (sidebar + topbar frame only). The shell uses a bright
 * pearl base with restrained lavender, aqua, and champagne reflections, composed
 * once here as ready-to-use gradient strings so makeInstitutionShellTokens and
 * InstitutionDashboardShell never scatter raw rgba soup inline.
 */
export const INSTITUTION_SHELL_LIGHT = {
  ivory: '#F8F8FC',
  pearl: '#FCFCFE',
  lavender: '#F0EBFC',
  aqua: '#E7F7F8',
  champagne: '#FBF4DF',
  blush: '#FBEAF3',
  // Neutral hairline for internal dividers (logo row, nav groups, sign-out).
  border: 'rgba(92,82,124,0.13)',
  // A visibly stronger violet-neutral edge for the sidebar's outer right border
  // and the topbar's bottom border -- the two edges that frame the shell.
  edgeBorder: 'rgba(112,88,164,0.2)',
  sectionLabel: '#675A93',
  shadow:
    '0 1px 2px rgba(45,40,70,0.04), 0 10px 30px rgba(91,74,142,0.07), 0 22px 55px rgba(113,90,164,0.04)',
  sidebarBg:
    'radial-gradient(circle at 20% 12%,rgba(185,165,255,0.15),transparent 35%),radial-gradient(circle at 80% 72%,rgba(124,216,224,0.1),transparent 38%),radial-gradient(circle at 40% 95%,rgba(239,196,114,0.08),transparent 32%),rgba(248,248,252,0.94)',
  topbar:
    'radial-gradient(circle at 18% 0%,rgba(200,182,255,0.14),transparent 34%),radial-gradient(circle at 78% 100%,rgba(169,229,234,0.11),transparent 38%),linear-gradient(90deg,rgba(252,252,254,0.94),rgba(255,255,255,0.88),rgba(252,250,254,0.94))',
} as const;

export const INSTITUTION_SHELL_DARK = {
  midnightA: '#0C1420',
  midnightB: '#101A2B',
  emerald: '#174C42',
  teal: '#145C59',
  indigo: '#25234E',
  gold: '#6E5324',
  border: 'rgba(113,145,161,0.16)',
  edgeBorder: 'rgba(49,148,128,0.22)',
  sectionLabel: '#7FA396',
  shadow: '0 8px 30px rgba(0,0,0,0.2)',
  sidebarBg:
    'radial-gradient(circle at 20% 15%,rgba(41,117,99,0.22),transparent 42%),radial-gradient(circle at 80% 55%,rgba(65,62,132,0.18),transparent 42%),radial-gradient(circle at 40% 95%,rgba(166,126,53,0.08),transparent 38%),linear-gradient(175deg,#101A2B 0%,#0C1420 60%,#0C1420 100%)',
  topbar:
    'linear-gradient(90deg,rgba(31,106,91,0.11),rgba(17,26,43,0.9) 45%,rgba(76,67,140,0.09)),#0F1827',
} as const;
